import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, X, Zap, ZapOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { captureFrame, readOrientation, watchOrientation } from "../lib/camera";

/**
 * In-app camera — a viewfinder inside TowHook instead of handing off to the
 * phone's camera app (Oct 2026).
 *
 * Three reasons it exists, all of them driver complaints:
 *
 *  1. The camera app saves its own copy to the gallery on most Android
 *     phones, so drivers were accumulating every job photo in their camera
 *     roll and deleting them by hand later. Nothing here ever touches the
 *     gallery.
 *  2. A 12-megapixel JPEG has to be decoded and re-encoded before it can be
 *     queued — 3-6 seconds of pure CPU on a budget phone. We grab the frame
 *     at the size we actually want, so there is nothing to shrink.
 *  3. iPhones can hand over HEIC, which the canvas path can't reliably
 *     decode; here we encode the JPEG ourselves.
 *
 * Capture is deliberately the same 1600px / q0.85 as `imageResize.js` so a
 * photo taken here and one picked from a file are indistinguishable to the
 * backend and to an insurer. The framing, sizing and rotation rules live in
 * `lib/camera.js` and are shared with the dispatcher's PhotoShop screen.
 */

// Oct 2026 — drivers got no confirmation a shot had landed (the count and
// thumbnails live in the modal BEHIND this overlay), so the natural response
// was to tap again. Two taps a second apart gave two near-identical frames,
// both counting toward the four-photo minimum, which quietly weakened the
// damage-claim set. Ignore a repeat tap inside this window.
const SHUTTER_LOCKOUT_MS = 900;

export default function InAppCamera({ open, count = 0, jobId = null, onCapture, onClose, onUnavailable }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lastShotAt = useRef(0);
  const trackRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastShot, setLastShot] = useState(null);
  const [orient, setOrient] = useState(readOrientation);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => setOrient(readOrientation());
    update();
    return watchOrientation(update);
  }, [open]);


  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    trackRef.current = null;
    setReady(false);
    setTorchOn(false);
    setHasTorch(false);
  }, []);

  useEffect(() => {
    if (!open) { stop(); return undefined; }
    let cancelled = false;

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("This browser can't open the camera");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },   // rear camera
            // 1080p, same as the dispatcher's PhotoShop camera. A 27 Oct
            // bump to 2560x1440 put the Samsungs into a high-resolution
            // sensor mode that hands back UN-ROTATED frames: the preview
            // stayed upright (Chromium rotates for display) while every
            // saved photo came out 90 degrees over. 264 driver photos in a
            // row stored portrait-shaped with sideways pixels, against a
            // healthy portrait/landscape mix from the 1080p PhotoShop path
            // on the very same phones. The megapixel worry that motivated
            // the bump was itself a symptom: a correctly-rotated 1080p
            // portrait frame crops to ~1.2MP, the same as before.
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        // Torch is Android-only in practice; iOS Safari doesn't expose it.
        setHasTorch(Boolean(track?.getCapabilities?.().torch));
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
        setError(denied
          ? "TowHook needs permission to use the camera. Allow it in your browser settings, or use your phone's camera instead."
          : "Couldn't open the camera on this phone.");
      }
    })();

    return () => { cancelled = true; stop(); };
  }, [open, stop]);

  // The preview is an object URL — let it go when the camera closes or the
  // component unmounts, or it pins the last frame's memory.
  useEffect(() => {
    if (open) return undefined;
    setLastShot((url) => { if (url) URL.revokeObjectURL(url); return null; });
    return undefined;
  }, [open]);

  useEffect(() => () => {
    setLastShot((url) => { if (url) URL.revokeObjectURL(url); return null; });
  }, []);

  // Toasts land top-right and span the full width on a phone, which put them
  // straight over the close button in the corner. Nudge them below the top
  // bar for as long as the viewfinder is up (see `body.camera-open` in
  // index.css) — they still need to be readable, so moving beats hiding.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("camera-open");
    return () => document.body.classList.remove("camera-open");
  }, [open]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      setHasTorch(false);
    }
  };

  /**
   * Record what the phone reported at the moment of the shot.
   *
   * Three rounds of fixing this blind is enough: nothing was ever stored
   * about the device, so every theory had to be argued from stored pixel
   * dimensions, which turned out to be identical under both the broken and
   * the fixed resolution. This records the facts instead — the frame the
   * camera handed us, the viewfinder it was cropped to, the rotation the
   * device reports, and which app build took it.
   *
   * Fire-and-forget and fully swallowed: a driver's photo must never fail
   * because a diagnostic did. Runs AFTER the photo is safely away.
   */
  const recordCaptureDiag = async (video, blob) => {
    try {
      const box = video.getBoundingClientRect();
      const settings = trackRef.current?.getSettings?.() || {};
      const bundle = document.querySelector('script[src*="/static/js/main."]')?.src || null;
      await api.post("/diag/capture", {
        job_id: jobId || null,
        source: "driver_at_scene",
        frame: { width: video.videoWidth, height: video.videoHeight },
        track: { width: settings.width ?? null, height: settings.height ?? null },
        viewfinder: { width: Math.round(box.width), height: Math.round(box.height) },
        orientation: {
          landscape: readOrientation().landscape,
          angle: (typeof window !== "undefined"
            && (window.screen?.orientation?.angle ?? window.orientation ?? null)),
          type: window.screen?.orientation?.type || null,
        },
        bytes: blob?.size ?? null,
        bundle: bundle ? bundle.split("/").pop() : null,
        user_agent: navigator.userAgent,
      });
    } catch { /* diagnostics must never disturb a capture */ }
  };

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || busy) return;
    if (Date.now() - lastShotAt.current < SHUTTER_LOCKOUT_MS) return;
    lastShotAt.current = Date.now();
    setBusy(true);
    try {
      const blob = await captureFrame(video);
      if (!blob) throw new Error("capture failed");
      setLastShot((url) => { if (url) URL.revokeObjectURL(url); return URL.createObjectURL(blob); });
      await onCapture(blob);
      recordCaptureDiag(video, blob);
    } catch {
      // A one-off encode failure shouldn't tear the viewfinder down and make
      // them start again — say so and leave them pointed at the vehicle.
      toast.error("That photo didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  // Portalled to <body>: the photos modal is a Radix dialog, and its
  // translate() transform makes `position: fixed` resolve against the
  // dialog box instead of the screen — the viewfinder came out as a small
  // rectangle inside the modal rather than full-bleed.
  return createPortal((
    <div
      className="fixed inset-0 z-[100] bg-black pointer-events-auto"
      data-testid="in-app-camera"
    >
      {error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400" />
          <p className="text-sm text-slate-200 max-w-xs" data-testid="in-app-camera-error">{error}</p>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            {onUnavailable && (
              <Button
                onClick={() => { setError(null); onUnavailable(); }}
                className="h-12 font-bold"
                data-testid="in-app-camera-fallback"
              >
                Use my phone's camera
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => { setError(null); onClose(); }}
              className="h-12"
              data-testid="in-app-camera-error-close"
            >
              Back
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Full-bleed. The shutter used to sit in a solid black bar below
              the picture, which ate a third of a short screen — it now floats
              over the bottom of the viewfinder on a soft scrim instead.
              `pointer-events-auto` on the parent matters: Radix puts
              pointer-events:none on <body> while its dialog is open, which
              left this portalled overlay untappable. */}
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="in-app-camera-video"
          />
          <div className="absolute top-3 inset-x-3 z-10 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center"
              aria-label="Close camera"
              data-testid="in-app-camera-close"
            >
              <X className="h-5 w-5" />
            </button>
            {hasTorch && (
              <button
                type="button"
                onClick={toggleTorch}
                className="h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center"
                aria-label="Toggle light"
                data-testid="in-app-camera-torch"
              >
                {torchOn ? <Zap className="h-5 w-5 text-amber-300" /> : <ZapOff className="h-5 w-5" />}
              </button>
            )}
          </div>

          <div
            className={`${orient.landscape
              ? `absolute inset-y-0 ${orient.side === "left" ? "left-0 bg-gradient-to-r" : "right-0 bg-gradient-to-l"} w-28 flex items-center justify-center from-black/70 via-black/30 to-transparent`
              : "absolute bottom-0 inset-x-0 pt-12 pb-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent"} pointer-events-none`}
            data-testid="in-app-camera-controls"
            data-placement={orient.landscape ? `side-${orient.side}` : "bottom"}
          >
            {/* The side strip is full height, so it covered the close button
                in the corner and swallowed the tap — a driver had to rotate
                the phone to get out of the camera. Only the controls
                themselves take pointer events; the scrim lets taps through. */}
            <div className="relative flex items-center justify-center pointer-events-auto">
              {/* Proof the shot landed. Half the shutter's size, and it keys
                  off the photo count so it re-animates on each capture.
                  Bottom-left of the frame when upright, directly above the
                  shutter when the controls move to a side edge. */}
              {lastShot && (
                <div
                  key={count}
                  className={`absolute h-10 w-10 animate-in zoom-in-50 fade-in duration-300 ${
                    orient.landscape ? "-top-[72px]" : "left-6"
                  }`}
                  data-testid="in-app-camera-last-shot"
                >
                  <img
                    src={lastShot}
                    alt="Last photo taken"
                    className="h-10 w-10 rounded-md object-cover border-2 border-white/90 shadow-lg"
                  />
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
                    data-testid="in-app-camera-count"
                  >
                    {count}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={shoot}
                disabled={!ready || busy}
                className="h-20 w-20 rounded-full bg-white border-4 border-slate-400 active:scale-95 transition-transform disabled:opacity-40 flex items-center justify-center"
                aria-label="Take photo"
                data-testid="in-app-camera-shutter"
              >
                <Camera className="h-8 w-8 text-slate-900" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  ), document.body);
}
